import { del, get, post, put } from './client';
import { ROUTES } from './config';
import type { ApiEnvelope } from '@/types/types';

export interface YearRange {
    start?: number | null;
    end?: number | null;
}

export interface PricebookRule {
    id: string;
    service_detail_id: number;
    price: number | null;
    rank?: number;
    year_range?: YearRange | null;
    make?: string[] | null;
    model?: string[] | null;
    includes_future_models?: boolean;
    special_order?: boolean;
    description?: string | null;
}

export interface Pricebook {
    id: string;
    name: string;
    company_name?: string;
    rules?: PricebookRule[];
}

export interface PricebookSearchParams {
    service_type: string;
    repair_type?: string;
    part?: string;
    position?: string;
    vin?: string;
    year_start?: number;
    year_end?: number;
    make?: string[];
    model?: string[];
    include_description?: boolean;
    page?: number;
    page_size?: number;
}

export interface EstimateRequest {
    vehicle: { vin: string };
    services: Array<{
        service_details: { service_type: string; repair_type?: string };
    }>;
}

export interface EstimatePrice {
    price: number | null;
    name?: string;
    rank?: number;
}

export interface EstimateLine {
    service_details?: {
        service_type?: string;
        repair_type?: string;
        part?: string;
        position?: string;
    };
    prices?: EstimatePrice[];
}

export interface EstimateService {
    estimates?: EstimateLine[];
}

export interface EstimateResult {
    vehicle?: {
        year?: number;
        make?: string;
        model?: string;
        vin?: string;
    };
    services?: EstimateService[];
}

export interface ConflictRuleUpdate {
    rule: PricebookRule;
    fieldsToUpdate: { price?: number | null };
}

export interface ConflictPayload {
    message?: string;
    payload?: {
        rulesToUpdate?: ConflictRuleUpdate[];
    };
}

export const pricebookApi = {
    listPricebooks: () => get<ApiEnvelope<Pricebook[]>>(ROUTES.PRICEBOOKS),
    searchPricebooks: (params: PricebookSearchParams) =>
        post<ApiEnvelope<PricebookRule[] | { rules?: PricebookRule[] }>>(
            ROUTES.PRICEBOOKS_SEARCH,
            params,
        ),
    estimate: (payload: EstimateRequest) =>
        post<ApiEnvelope<EstimateResult>>(ROUTES.ESTIMATE, payload),
    listRules: (pricebookId: string) =>
        get<ApiEnvelope<PricebookRule[]>>(ROUTES.PRICEBOOK_RULES(pricebookId)),
    addRule: (
        pricebookId: string,
        body: { rules: Partial<PricebookRule>[]; force?: boolean },
    ) =>
        post<ApiEnvelope<PricebookRule[]>>(
            ROUTES.PRICEBOOK_RULE(pricebookId),
            body,
        ),
    updateRule: (
        pricebookId: string,
        ruleId: string,
        body: { rules: Partial<PricebookRule>[]; force?: boolean },
    ) =>
        put<ApiEnvelope<PricebookRule[]>>(
            ROUTES.PRICEBOOK_RULE_UPDATE(pricebookId, ruleId),
            body,
        ),
    deleteRule: (pricebookId: string, ruleId: string) =>
        del<void>(ROUTES.PRICEBOOK_RULE_DELETE(pricebookId, ruleId)),
    getDocumentation: () => get<ApiEnvelope<string>>(ROUTES.PRICEBOOK_DOCUMENTATION),
    getVehicleMakes: () => get<ApiEnvelope<string[]>>(ROUTES.VEHICLE_MAKES),
    getVehicleModels: (makes?: string[]) => {
        if (Array.isArray(makes) && makes.length > 0) {
            return get<ApiEnvelope<string[]>>(ROUTES.VEHICLE_MODELS, {
                params: { makes: makes.join(',') },
            });
        }
        return get<ApiEnvelope<string[]>>(ROUTES.VEHICLE_MODELS);
    },
};
